import { useCallback, useMemo } from 'react';
import ValidationService from '../services';

const useScheduleValidation = (allSchedulesCollection, validationErrors, requiredFields) => {
  const validateAllSchedules = useCallback(() => {
    return ValidationService.validateScheduleCollection(
      allSchedulesCollection,
      validationErrors,
      requiredFields,
    );
  }, [allSchedulesCollection, validationErrors, requiredFields]);

  const validationResult = useMemo(() => {
    return validateAllSchedules();
  }, [allSchedulesCollection, validationErrors, requiredFields]);

  const getAllRequiredFieldsForAllGroups = useCallback(() => {
    const allGroups = ['fix_and_flips', 'investment_properties_owned', 'primary_residence'];
    const allRequiredFields = {};

    allGroups.forEach((groupName) => {
      allRequiredFields[groupName] = requiredFields(groupName);
    });

    return allRequiredFields;
  }, [requiredFields]);

  const validateAllSchedulesCollection = useCallback(() => {
    const realSchedules = allSchedulesCollection.filter((schedule) => (
      schedule.id &&
      schedule.id !== '' &&
      !schedule._new &&
      !schedule._emptyRowId &&
      Object.keys(schedule).some((key) => (
        !['_new', '_emptyRowId', 'key'].includes(key) &&
        schedule[key] &&
        schedule[key].toString().trim() !== ''
      ))
    ));

    if (!realSchedules || realSchedules.length === 0) {
      return {
        isValid: false,
        message: 'Please add at least one property to complete the schedule.',
        missingFieldsByGroup: {},
        totalMissingFields: 0,
      };
    }

    const allRequiredFields = getAllRequiredFieldsForAllGroups();
    const missingFieldsByGroup = {};
    let totalMissingFields = 0;
    const hasValidationErrors = Object.keys(validationErrors).length > 0;

    const groupedSchedules = realSchedules.reduce((acc, schedule) => {
      const scheduleType = schedule.group;
      if (!scheduleType) {
        return acc;
      }

      if (!acc[scheduleType]) {
        acc[scheduleType] = [];
      }

      acc[scheduleType].push(schedule);

      return acc;
    }, {});

    Object.keys(groupedSchedules).forEach((groupType) => {
      const schedules = groupedSchedules[groupType];
      const requiredFieldsForGroup = allRequiredFields[groupType] || [];

      missingFieldsByGroup[groupType] = [];

      schedules.forEach((schedule, index) => {
        requiredFieldsForGroup.forEach((field) => {
          const value = schedule[field];
          if (!value || value.toString().trim() === '') {
            missingFieldsByGroup[groupType].push({
              rowIndex: index + 1,
              field,
              id: schedule.id,
            });
            totalMissingFields += 1;
          }
        });
      });
    });

    const hasEmptyFields = totalMissingFields > 0;

    let message = null;
    if (hasValidationErrors) {
      message = 'Please fix validation errors before submitting.';
    } else if (hasEmptyFields) {
      message = 'All required fields must be completed before proceeding.';
    }

    return {
      isValid: !hasEmptyFields && !hasValidationErrors,
    };
  }, [allSchedulesCollection, validationErrors, getAllRequiredFieldsForAllGroups]);

  const getMissingFieldsMap = useCallback(() => {
    return ValidationService.createMissingFieldsMap(validationResult);
  }, [validationResult]);

  const getDuplicateAddressesMap = useCallback(() => {
    return ValidationService.createDuplicateAddressesMap(validationResult.duplicateAddresses || []);
  }, [validationResult]);

  const isFieldMissing = useCallback((record, fieldName) => {
    const missingFieldsMap = getMissingFieldsMap();
    const fieldKey = `${record.id || record._emptyRowId}_${fieldName}`;

    return missingFieldsMap[fieldKey] || false;
  }, [getMissingFieldsMap]);

  const isDuplicateAddress = useCallback((record, fieldName) => {
    return ValidationService.isDuplicateAddress(record, fieldName, validationResult.duplicateAddresses || []);
  }, [validationResult]);

  return {
    isFieldMissing,
    isDuplicateAddress,
    validateAllSchedulesCollection,
    getDuplicateAddressesMap,
  };
};

export default useScheduleValidation;
